/**
 * Combobox — a typeable autocomplete dropdown.
 *
 *  Props:
 *    options       Array<{ value: string, label: string }>   required
 *    value         string                                    currently-selected value
 *    onChange(v)   callback when an option is picked
 *    placeholder   string
 *    allowCustom   boolean — if true, user can type a value not in the list and it
 *                  will be returned via onChange on blur / Enter
 *    testid        string for data-testid
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

export default function Combobox({
  options = [],
  value = '',
  onChange,
  placeholder = 'Type to search...',
  allowCustom = false,
  testid,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  // Sync query with the currently-selected value's label
  useEffect(() => {
    const selected = options.find((o) => o.value === value);
    setQuery(selected ? selected.label : value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const pick = (opt) => {
    setQuery(opt.label);
    onChange(opt.value);
    setOpen(false);
  };

  const handleBlurCommit = () => {
    if (!allowCustom) return;
    // If query doesn't match any existing option exactly, send it as the value
    const exact = options.find((o) => o.label === query);
    if (exact) onChange(exact.value);
    else if (query.trim()) onChange(query.trim());
  };

  const clear = () => {
    setQuery('');
    onChange('');
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(handleBlurCommit, 100)}
          placeholder={placeholder}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-16 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={testid}
          autoComplete="off"
        />
        <div className="absolute right-1 top-0 h-10 flex items-center gap-1">
          {query && (
            <button
              type="button"
              onClick={clear}
              className="p-1 hover:bg-muted rounded text-muted-foreground"
              aria-label="Clear"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="p-1 hover:bg-muted rounded text-muted-foreground"
            aria-label="Toggle"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-input bg-popover shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {allowCustom ? `Use "${query}"` : 'No matches'}
            </div>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(opt)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${
                  opt.value === value ? 'bg-muted font-medium' : ''
                }`}
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
